//React
import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
//Libraries
import cx from "classnames";
//Components
import {
  Card,
  DocsCodeBlock,
  DocsFlexSectionBlock,
  DocsScreen,
  FlexBox,
  TitleBlock,
  Image,
} from "components";
//Constants
import { docsConstants } from "../../../constants";
//Utils
import { useIsInViewport } from "utils";
//Styles
import "./styles.scss";
//Assets
import JavaIcon from "assets/icons/java.png";

const DocsHome = () => {
  /* Constants */
  //States
  const [scrolledToElement, setScrolledToElement]: any = useState({
    selectedTab: docsConstants.TITLES.OVERVIEW,
  });

  //Params
  const sdkType: any = useParams();

  //Refs
  const WelcomeSectionRef: any = useRef();
  const isWelcomeSectionInViewport = useIsInViewport(WelcomeSectionRef);
  //Javascript Sdk
  const InstallRef: any = useRef();
  const isInstallInViewport = useIsInViewport(InstallRef);
  const InitializeRef: any = useRef();
  const isInitializeInViewport = useIsInViewport(InitializeRef);
  const MethodsRef: any = useRef();
  const isMethodsInViewport = useIsInViewport(MethodsRef);

  //Jsons
  const scrollAccToSpecificElementMap = {
    [docsConstants.TITLES.OVERVIEW]: WelcomeSectionRef,
    [docsConstants.TITLES.JAVASCRIPT.INSTALL]: InstallRef,
    [docsConstants.TITLES.JAVASCRIPT.INITIALISE]: InitializeRef,
    [docsConstants.TITLES.JAVASCRIPT.METHOD]: MethodsRef,
  };

  /* useEffects */
  useEffect(() => {
    if (isWelcomeSectionInViewport) {
      setScrolledToElement({
        selectedTab: docsConstants.TITLES.OVERVIEW,
      });
    } else if (isInstallInViewport) {
      setScrolledToElement({
        selectedTab: docsConstants.TITLES.JAVASCRIPT.INSTALL,
        selectedParentTab: docsConstants.TITLES.LOGIN_WITH_WHATSAPP,
        selectedGRandParentTab: docsConstants.TITLES.SDK_AND_API_REFERENCE,
      });
    } else if (isInitializeInViewport) {
      setScrolledToElement({
        selectedTab: docsConstants.TITLES.JAVASCRIPT.INITIALISE,
        selectedParentTab: docsConstants.TITLES.LOGIN_WITH_WHATSAPP,
        selectedGRandParentTab: docsConstants.TITLES.SDK_AND_API_REFERENCE,
      });
    } else if (isMethodsInViewport) {
      setScrolledToElement({
        selectedTab: docsConstants.TITLES.JAVASCRIPT.METHOD,
        selectedParentTab: docsConstants.TITLES.LOGIN_WITH_WHATSAPP,
        selectedGRandParentTab: docsConstants.TITLES.SDK_AND_API_REFERENCE,
      });
    }
  }, [
    isWelcomeSectionInViewport,
    //javascript sdk
    isInstallInViewport,
    isInitializeInViewport,
    isMethodsInViewport,
  ]);

  /* Functions */
  //Scroll To Specific element
  const scrollToSpecificElement = (selectedTab) => {
    scrollAccToSpecificElementMap[selectedTab]?.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
      inline: "nearest",
    });
  };

  //Render UI
  const WelcomeSection = (
    <div ref={WelcomeSectionRef}>
      <TitleBlock
        title={
          <FlexBox alignCenter className="section-header">
            <Image src={JavaIcon} className="mr-r-10" />
            <p>Javascript SDK</p>
          </FlexBox>
        }
        content={
          <p className="fs-16">
            The JavaScript SDK provides methods that communicate directly with
            the OTPless API. These help you get up and running with OTPless
            faster by avoiding the struggles associated with to create endpoints
            on your backend to make requests to OTPless.
          </p>
        }
        titleClass="fs-32 font-heavy"
        className="animated fadeInUp mr-b-60"
      />
    </div>
  );

  const JavascriptSdk = () => {
    const Link = (
      <>
        <div ref={InstallRef}>
          <TitleBlock
            title={`1. ${docsConstants.TITLES.JAVASCRIPT.INSTALL}`}
            content={
              <DocsFlexSectionBlock
                leftSection={
                  <div className="fs-16">
                    <p>Install the otpless-js-sdk from the npm repository.</p>
                  </div>
                }
                rightSection={
                  <DocsCodeBlock
                    title={"Install"}
                    codeToBeCopied={`npm i otpless-js-sdk`}
                    code={
                      <>
                        <p>
                          <span className="tag">npm</span>
                          <span className="attr-name mr-h-5">i</span>
                          <span className="attr-value">otpless-js-sdk</span>
                        </p>
                      </>
                    }
                  />
                }
              />
            }
            titleClass="fs-24 font-heavy"
            className="animated fadeInUp delay-01s mr-b-60"
          />
        </div>
        <div ref={InitializeRef}>
          <TitleBlock
            title={`2. ${docsConstants.TITLES.JAVASCRIPT.INITIALISE}`}
            content={
              <DocsFlexSectionBlock
                leftSection={
                  <div className="fs-16">
                    <p>
                      Initialize the sdk.{" "}
                      <span className="highlighted font-light pd-h-5">
                        appId
                      </span>{" "}
                      is required.{" "}
                      <span className="highlighted font-light pd-h-5">
                        enableErrorLogging
                      </span>{" "}
                      is an optional parameter. On setting it to true, the error
                      logs are consoled to the stderr.
                    </p>
                  </div>
                }
                rightSection={
                  <DocsCodeBlock
                    title={"Initialize"}
                    codeToBeCopied={`
  import OtplessSdk from "otpless-js-sdk";
  const sdkIntance = new OtplessSdk(
    {
        appId: "YOUR_APP_ID",
        enableErrorLogging: true
    }
  );
                    `}
                    code={
                      <>
                        <p>
                          <span className="punctuation">import</span>
                          <span className="attr-name mr-l-5">OtplessSdk</span>
                          <span className="tag mr-h-5">from</span>
                          <span className="attr-value">"otpless-js-sdk"</span>;
                        </p>
                        <p>
                          <span className="punctuation">const</span>
                          <span className="attr-name mr-l-5">sdkIntance</span>
                          <span className="tag mr-h-5">=</span>
                          <span className="attr-name mr-r-5">new</span>
                          <span className="attr-value">OtplessSdk</span>
                          <span className="tag mr-h-5">(</span>
                        </p>
                        <p className="mr-l-20 mr-t-5 tag">&#123;</p>
                        <p className="mr-l-40 mr-t-5">
                          <span className="attr-name mr-h-5">"appId"</span>:{" "}
                          <span className="attr-value mr-h-5">
                            "YOUR_APP_ID"
                          </span>
                        </p>
                        <p className="mr-l-40 mr-t-5">
                          <span className="attr-name mr-h-5">
                            "enableErrorLogging"
                          </span>
                          : <span className="attr-value mr-h-5">true</span>
                        </p>
                        <p className="mr-l-20 mr-t-5 tag">&#125;</p>
                        <span className="tag">)</span>;
                      </>
                    }
                  />
                }
                className="mr-t-40"
              />
            }
            titleClass="fs-24 font-heavy"
            className="animated fadeInUp delay-01s mr-b-60 dashed-border-top pd-t-30"
          />
        </div>
        <div ref={MethodsRef}>
          <TitleBlock
            title={`3. ${docsConstants.TITLES.JAVASCRIPT.METHOD}`}
            content={
              <>
                <DocsFlexSectionBlock
                  leftSection={
                    <div className="fs-16">
                      <p>
                        create an Onclick function using{" "}
                        <span className="highlighted font-light pd-h-5">
                          createGetIntentOnClick
                        </span>
                        . It takes{" "}
                        <span className="highlighted font-light pd-h-5">
                          redirectionURL
                        </span>{" "}
                        object property as a parameter. This is the url to which
                        the client is redirected to once it gets a response in
                        WhatsApp.
                      </p>
                    </div>
                  }
                  rightSection={
                    <DocsCodeBlock
                      title={"Create"}
                      codeToBeCopied={`
  <button
    onClick={sdkIntance.createGetIntentOnClick({
      redirectionURL: "YOUR_REDIRECTION_URL",
    })}
  >
    Login with WhatsApp
  </button>
                    `}
                      code={
                        <>
                          <p>
                            {" "}
                            <span className="comment">&#60;</span>
                            <span className="tag">button</span>
                          </p>
                          <p className="mr-l-20 mr-t-5">
                            <span className="attr-name">onClick</span>
                            <span className="tag mr-h-5">=</span>
                            <span className="tag">&#123;</span>
                            <span className="attr-name">sdkIntance</span>.
                            <span className="attr-value">
                              createGetIntentOnClick
                            </span>
                            <span className="tag mr-h-5">(&#123;</span>
                          </p>
                          <p className="mr-l-40 mr-t-5">
                            <span className="attr-name mr-h-5">
                              "redirectionURL"
                            </span>
                            :{" "}
                            <span className="attr-value mr-h-5">
                              "YOUR_REDIRECTION_URL"
                            </span>
                          </p>
                          <p className="tag mr-l-20 mr-t-5">&#125;)&#125;</p>
                          <span className="comment">&#62;</span>
                          <p className="mr-l-20">Login with WhatsApp</p>
                          <p>
                            <span className="comment">&#60;/</span>
                            <span className="tag">button</span>
                            <span className="comment">&#62;</span>
                          </p>
                        </>
                      }
                    />
                  }
                  className="mr-t-40"
                />
                <DocsFlexSectionBlock
                  leftSection={
                    <div className="fs-16">
                      <p>
                        After successfull redirection from WhatsApp, extract the
                        token from query params using the{" "}
                        <span className="font-medium">static</span> function{" "}
                        <span className="highlighted font-light pd-h-5">
                          getTokenFromQueryParams
                        </span>{" "}
                        and the state from the local storage using the{" "}
                        <span className="font-medium">static</span> function{" "}
                        <span className="highlighted font-light pd-h-5">
                          getState
                        </span>{" "}
                        . The token along with the state needs to be passed to
                        your server which in turn should make call to OTPless
                        server for user details.
                      </p>
                    </div>
                  }
                  rightSection={
                    <DocsCodeBlock
                      title={"Token"}
                      codeToBeCopied={`
  const token=OtplessSdk.getToken();
  const state=OtplessSdk.getState()
                    `}
                      code={
                        <>
                          <p>
                            <span className="punctuation">const</span>
                            <span className="attr-name mr-l-5">token</span>
                            <span className="tag mr-h-5">=</span>
                            <span className="attr-value">OtplessSdk</span>.
                            <span className="attr-value">getToken</span>
                            <span className="tag">()</span>;
                          </p>
                          <p className="mr-t-5">
                            <span className="punctuation">const</span>
                            <span className="attr-name mr-l-5">state</span>
                            <span className="tag mr-h-5">=</span>
                            <span className="attr-value">OtplessSdk</span>.
                            <span className="attr-value">getState</span>
                            <span className="tag">()</span>;
                          </p>
                        </>
                      }
                    />
                  }
                  className="mr-t-40"
                />
              </>
            }
            titleClass="fs-24 font-heavy"
            className="animated fadeInUp delay-01s mr-b-60 dashed-border-top pd-t-30"
          />
        </div>
      </>
    );

    return (
      <TitleBlock
        title={""}
        content={<>{Link}</>}
        titleClass="fs-24 font-heavy"
        className="animated fadeInUp delay-01s border-top mr-b-60 pd-t-30 javascript-sdk"
      />
    );
  };

  //Return UI
  return (
    <DocsScreen
      className="docs-home-screen"
      hasSideMenu
      hasHeader
      screenAnimationClass="no-animation"
      tabSelectCallback={scrollToSpecificElement}
      scrolledToElement={{ ...scrolledToElement }}
    >
      {WelcomeSection}
      {JavascriptSdk()}
    </DocsScreen>
  );
};

export default DocsHome;
